import { Business, Service, ServiceSellPrice, ServiceSellCost, DEFAULT_CURRENCY, PRICE_BY_OPTIONS } from "@turizi/shared";
import { useTranslation } from 'react-i18next';
import { ActionIcon, Select, Switch, Loader, Tooltip } from '@mantine/core';
import { useDeleteServiceSellCost, useDeleteServiceSellPrice, useUpdateService } from "@/hooks/api-hooks";
import {
    IconScale,
    IconArrowUp,
    IconArrowDown,
    IconTrash,
    IconTrashX,
    IconCornerDownRight,
    IconChevronDown,
    IconChevronRight,
    IconChevronsDown,
    IconChevronsUp,
} from '@tabler/icons-react';
import { useState, useMemo, memo, Fragment } from 'react';
import { formatDay, formatPrice, todayStart } from "@/lib/format-utils";
import dayjs from 'dayjs';
import { TagIcon } from "@/lib/tag-utils";
import EditSinglePrice from "./edit-single-price";
import EditSingleCost from "./edit-single-cost";
import ServiceNewPrice from "./new-price-section";
import ServiceNewCost from "./new-cost-section";

export type Props = {
    service: Service;
};

export interface TierComparison {
    tier: 'default' | 'child' | 'senior' | 'infant' | 'foreigner';
    labelKey: string;
    priceAmount: number | null;
    costAmount: number | null;
    priceCurrency: string;
    costCurrency: string;
    difference: number | null;
    marginPercent: number | null;
    markupPercent: number | null;
    isDifferentCurrencies: boolean;
    hasLoss: boolean;
}

export interface ProviderComparison {
    providerId: number | null;
    provider: Business | null;
    cost: ServiceSellCost | null;
    defaultPriceAmount: number | null;
    defaultCostAmount: number | null;
    priceCurrency: string;
    costCurrency: string;
    difference: number | null;
    marginPercent: number | null;
    markupPercent: number | null;
    isDifferentCurrencies: boolean;
    hasLoss: boolean;
    tiers: TierComparison[];
}

export interface UnifiedPeriod {
    id: string;
    start_day: string | null;
    end_day: string | null;
    durationDays: number | null;
    isCurrent: boolean;
    isPast: boolean;
    isFuture: boolean;
    price: ServiceSellPrice | null;
    priceTiers: TierComparison[];
    comparisons: ProviderComparison[];
}

function calculateTierComparison(
    tier: 'default' | 'child' | 'senior' | 'infant' | 'foreigner',
    labelKey: string,
    priceAmount: number | null | undefined,
    costAmount: number | null | undefined,
    priceCurrency: string,
    costCurrency: string
): TierComparison | null {
    const hasPrice = priceAmount !== null && priceAmount !== undefined && (tier === 'default' ? true : priceAmount > 0);
    const hasCost = costAmount !== null && costAmount !== undefined && (tier === 'default' ? true : costAmount > 0);

    if (!hasPrice && !hasCost) return null;

    const pAmt = hasPrice ? Number(priceAmount) : null;
    const cAmt = hasCost ? Number(costAmount) : null;
    const isDifferentCurrencies = Boolean(pAmt !== null && cAmt !== null && priceCurrency && costCurrency && priceCurrency !== costCurrency);

    let difference: number | null = null;
    let marginPercent: number | null = null;
    let markupPercent: number | null = null;
    let hasLoss = false;

    if (pAmt !== null && cAmt !== null && !isDifferentCurrencies) {
        difference = pAmt - cAmt;
        if (pAmt > 0) {
            marginPercent = (difference / pAmt) * 100;
        }
        if (cAmt > 0) {
            markupPercent = (difference / cAmt) * 100;
        }
        if (difference < 0) {
            hasLoss = true;
        }
    }

    return {
        tier,
        labelKey,
        priceAmount: pAmt,
        costAmount: cAmt,
        priceCurrency: priceCurrency || DEFAULT_CURRENCY,
        costCurrency: costCurrency || DEFAULT_CURRENCY,
        difference,
        marginPercent,
        markupPercent,
        isDifferentCurrencies,
        hasLoss,
    };
}

function buildTierComparisons(
    activePrice: ServiceSellPrice | null,
    cost: ServiceSellCost | null,
    priceCurrency: string,
    costCurrency: string,
    showRetired?: boolean
): TierComparison[] {
    const tierList: TierComparison[] = [];
    const priceDefault = activePrice?.default_amount ?? null;
    const costDefault = cost?.default_amount ?? null;

    const defaultTier = calculateTierComparison('default', 'default', priceDefault, costDefault, priceCurrency, costCurrency);
    if (defaultTier) tierList.push(defaultTier);

    const childTier = calculateTierComparison('child', 'children', activePrice?.child_amount, cost?.child_amount, activePrice?.child_currency || priceCurrency, cost?.child_currency || costCurrency);
    if (childTier) tierList.push(childTier);

    const seniorTier = calculateTierComparison('senior', showRetired ? 'retired_plural' : 'seniors', activePrice?.senior_amount, cost?.senior_amount, activePrice?.senior_currency || priceCurrency, cost?.senior_currency || costCurrency);
    if (seniorTier) tierList.push(seniorTier);

    const infantTier = calculateTierComparison('infant', 'infants', activePrice?.infant_amount, cost?.infant_amount, activePrice?.infant_currency || priceCurrency, cost?.infant_currency || costCurrency);
    if (infantTier) tierList.push(infantTier);

    const foreignerTier = calculateTierComparison('foreigner', 'foreigner', activePrice?.foreigner_amount, cost?.foreigner_amount, activePrice?.foreigner_currency || priceCurrency, cost?.foreigner_currency || costCurrency);
    if (foreignerTier) tierList.push(foreignerTier);

    return tierList;
}

function rangesOverlap(
    startA: string | null | undefined,
    endA: string | null | undefined,
    startB: string | null | undefined,
    endB: string | null | undefined
): boolean {
    const sA = startA || '0000-01-01';
    const eA = endA || '9999-12-31';
    const sB = startB || '0000-01-01';
    const eB = endB || '9999-12-31';
    return sA <= eB && eA >= sB;
}

function buildProviderComparison(
    activePrice: ServiceSellPrice | null,
    cost: ServiceSellCost,
    showRetired?: boolean
): ProviderComparison {
    const priceCurrency = activePrice?.default_currency || DEFAULT_CURRENCY;
    const priceDefault = activePrice?.default_amount ?? null;
    const costCurrency = cost.default_currency || DEFAULT_CURRENCY;
    const costDefault = cost.default_amount ?? null;
    const isDiffCurr = Boolean(priceDefault !== null && costDefault !== null && priceCurrency !== costCurrency);

    let diff: number | null = null;
    let marginPct: number | null = null;
    let markupPct: number | null = null;
    let hasLoss = false;

    if (priceDefault !== null && costDefault !== null && !isDiffCurr) {
        diff = priceDefault - costDefault;
        if (priceDefault > 0) {
            marginPct = (diff / priceDefault) * 100;
        }
        if (costDefault > 0) {
            markupPct = (diff / costDefault) * 100;
        }
        if (diff < 0) {
            hasLoss = true;
        }
    }

    return {
        providerId: cost.business_id ?? null,
        provider: cost.business ?? null,
        cost,
        defaultPriceAmount: priceDefault,
        defaultCostAmount: costDefault,
        priceCurrency,
        costCurrency,
        difference: diff,
        marginPercent: marginPct,
        markupPercent: markupPct,
        isDifferentCurrencies: isDiffCurr,
        hasLoss,
        tiers: buildTierComparisons(activePrice, cost, priceCurrency, costCurrency, showRetired),
    };
}

export default memo(function EditServicePricesAndCostsTab({ service }: Props) {
    const { t } = useTranslation();
    const today = todayStart();
    const updateServiceMutation = useUpdateService();
    const deletePriceMutation = useDeleteServiceSellPrice();
    const deleteCostMutation = useDeleteServiceSellCost();

    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'upcoming' | 'past'>('all');
    const [sortBy, setSortBy] = useState<'date' | 'price' | 'cost'>('date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [savingField, setSavingField] = useState<'price_by' | 'cost_by' | 'show_retired' | null>(null);
    const [collapsedPeriodIds, setCollapsedPeriodIds] = useState<Set<string>>(() => new Set());
    const [deletingAllCostsPeriodId, setDeletingAllCostsPeriodId] = useState<string | null>(null);

    const handleDeleteAllCosts = async (period: UnifiedPeriod) => {
        const costIds = period.comparisons
            .map(c => c.cost?.id)
            .filter((id): id is number => Boolean(id));
        if (costIds.length === 0) return;

        setDeletingAllCostsPeriodId(period.id);
        try {
            await Promise.all(costIds.map(id => deleteCostMutation.mutateAsync(id)));
        } finally {
            setDeletingAllCostsPeriodId(null);
        }
    };

    // Valid prices & costs
    const validPrices = useMemo(() => {
        return (service.sell_prices || []).filter(
            p => p.default_amount !== null && p.default_amount !== undefined
        );
    }, [service.sell_prices]);

    const validCosts = useMemo(() => {
        return (service.sell_costs || []).filter(
            c => c.default_amount !== null && c.default_amount !== undefined
        );
    }, [service.sell_costs]);

    // Build sell-price-centric periods, each with its overlapping provider costs
    const unifiedPeriods = useMemo(() => {
        if (validPrices.length === 0 && validCosts.length === 0) {
            return [];
        }

        const matchedCostIds = new Set<number>();
        const periods: UnifiedPeriod[] = [];

        validPrices.forEach(price => {
            const start = price.start_day || null;
            const end = price.end_day || null;
            const priceCurrency = price.default_currency || DEFAULT_CURRENCY;

            const overlappingCosts = validCosts
                .filter(c => rangesOverlap(start, end, c.start_day, c.end_day))
                .sort((a, b) => {
                    const nameA = a.business?.name || '';
                    const nameB = b.business?.name || '';
                    if (nameA !== nameB) return nameA.localeCompare(nameB);
                    return (a.start_day || '').localeCompare(b.start_day || '');
                });

            overlappingCosts.forEach(c => {
                if (c.id) matchedCostIds.add(c.id);
            });

            const comparisons = overlappingCosts.map(cost =>
                buildProviderComparison(price, cost, service.show_retired)
            );

            const isCurrent = (!start || start <= today) && (!end || end >= today);
            const isPast = end ? dayjs(end).isBefore(today, 'day') : false;
            const isFuture = start ? dayjs(start).isAfter(today, 'day') : false;
            const durationDays = start && end ? dayjs(end).diff(dayjs(start), 'day') + 1 : null;

            periods.push({
                id: `price_${price.id || `${start}_${end}`}`,
                start_day: start,
                end_day: end,
                durationDays,
                isCurrent,
                isPast,
                isFuture,
                price,
                priceTiers: buildTierComparisons(price, null, priceCurrency, priceCurrency, service.show_retired),
                comparisons,
            });
        });

        // Include any orphan costs that do not overlap with any sell price
        const orphanCosts = validCosts.filter(c => !c.id || !matchedCostIds.has(c.id));
        const orphanGroups = new Map<string, ServiceSellCost[]>();

        orphanCosts.forEach(cost => {
            const key = `${cost.start_day || 'any'}_${cost.end_day || 'any'}`;
            const group = orphanGroups.get(key) || [];
            group.push(cost);
            orphanGroups.set(key, group);
        });

        orphanGroups.forEach((costs, key) => {
            const first = costs[0];
            const start = first.start_day || null;
            const end = first.end_day || null;
            const isCurrent = (!start || start <= today) && (!end || end >= today);
            const isPast = end ? dayjs(end).isBefore(today, 'day') : false;
            const isFuture = start ? dayjs(start).isAfter(today, 'day') : false;
            const durationDays = start && end ? dayjs(end).diff(dayjs(start), 'day') + 1 : null;

            periods.push({
                id: `orphan_${key}`,
                start_day: start,
                end_day: end,
                durationDays,
                isCurrent,
                isPast,
                isFuture,
                price: null,
                priceTiers: [],
                comparisons: costs.map(c => buildProviderComparison(null, c, service.show_retired)),
            });
        });

        return periods;
    }, [validPrices, validCosts, today, service.show_retired]);

    // KPI Metrics
    const kpis = useMemo(() => {
        let activePeriod: UnifiedPeriod | null = null;
        const validMargins: number[] = [];

        unifiedPeriods.forEach(p => {
            if (p.isCurrent && !activePeriod) {
                activePeriod = p;
            }
            p.comparisons.forEach(c => {
                if (c.marginPercent !== null && !isNaN(c.marginPercent)) {
                    validMargins.push(c.marginPercent);
                }
            });
        });

        const totalCount = unifiedPeriods.length;
        const activeCount = unifiedPeriods.filter(p => p.isCurrent).length;
        const upcomingCount = unifiedPeriods.filter(p => p.isFuture).length;
        const pastCount = unifiedPeriods.filter(p => p.isPast).length;

        const avgMargin = validMargins.length > 0
            ? validMargins.reduce((a, b) => a + b, 0) / validMargins.length
            : null;

        return {
            activePeriod,
            totalCount,
            activeCount,
            upcomingCount,
            pastCount,
            avgMargin,
        };
    }, [unifiedPeriods]);

    // Filter and sort
    const filteredPeriods = useMemo(() => {
        let list = [...unifiedPeriods];

        if (statusFilter === 'active') {
            list = list.filter(p => p.isCurrent);
        } else if (statusFilter === 'upcoming') {
            list = list.filter(p => p.isFuture);
        } else if (statusFilter === 'past') {
            list = list.filter(p => p.isPast);
        }

        list.sort((a, b) => {
            if (sortBy === 'date') {
                const dateA = a.start_day || a.end_day || '';
                const dateB = b.start_day || b.end_day || '';
                if (!dateA && !dateB) return 0;
                if (!dateA) return sortOrder === 'asc' ? 1 : -1;
                if (!dateB) return sortOrder === 'asc' ? -1 : 1;
                const comp = dateA.localeCompare(dateB);
                return sortOrder === 'asc' ? comp : -comp;
            } else if (sortBy === 'price') {
                const aPrice = a.price?.default_amount ?? -9999;
                const bPrice = b.price?.default_amount ?? -9999;
                return sortOrder === 'asc' ? aPrice - bPrice : bPrice - aPrice;
            } else {
                const aCost = a.comparisons[0]?.defaultCostAmount ?? -9999;
                const bCost = b.comparisons[0]?.defaultCostAmount ?? -9999;
                return sortOrder === 'asc' ? aCost - bCost : bCost - aCost;
            }
        });

        return list;
    }, [unifiedPeriods, statusFilter, sortBy, sortOrder]);

    const handleSort = (col: 'date' | 'price' | 'cost') => {
        if (sortBy === col) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(col);
            setSortOrder('desc');
        }
    };

    const allCollapsed = useMemo(() => {
        if (filteredPeriods.length === 0) return false;
        return filteredPeriods.every(p => collapsedPeriodIds.has(p.id));
    }, [filteredPeriods, collapsedPeriodIds]);

    const togglePeriodCollapse = (periodId: string) => {
        setCollapsedPeriodIds(prev => {
            const next = new Set(prev);
            if (next.has(periodId)) {
                next.delete(periodId);
            } else {
                next.add(periodId);
            }
            return next;
        });
    };

    const toggleAllCosts = () => {
        if (allCollapsed) {
            setCollapsedPeriodIds(new Set());
        } else {
            setCollapsedPeriodIds(new Set(filteredPeriods.map(p => p.id)));
        }
    };

    const renderSortHeader = (label: string, col: 'date' | 'price' | 'cost', alignRight = false) => {
        const isActive = sortBy === col;
        return (
            <button
                type="button"
                onClick={() => handleSort(col)}
                className={`inline-flex items-center gap-1 cursor-pointer select-none font-semibold hover:text-gray-900 transition-colors ${alignRight ? 'justify-end w-full' : ''} ${isActive ? 'text-gray-900' : 'text-gray-500'}`}
            >
                <span>{label}</span>
                {isActive && (
                    sortOrder === 'asc' ? <IconArrowUp size={12} className="text-gray-900 shrink-0" /> : <IconArrowDown size={12} className="text-gray-900 shrink-0" />
                )}
            </button>
        );
    };

    const tierColumns: Array<{ key: TierComparison['tier']; labelKey: string }> = [
        { key: 'default', labelKey: 'default' },
        { key: 'child', labelKey: 'children' },
        { key: 'senior', labelKey: service.show_retired ? 'retired_plural' : 'seniors' },
        { key: 'infant', labelKey: 'infants' },
        { key: 'foreigner', labelKey: 'foreigner' },
    ];

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-4 pb-3 border-b border-gray-200">
                <Select
                    required
                    error={!service.price_by}
                    size="xs"
                    className="w-44"
                    label={t('price_by')}
                    value={service.price_by}
                    disabled={savingField === 'price_by' && updateServiceMutation.isPending}
                    rightSection={savingField === 'price_by' && updateServiceMutation.isPending ? <Loader size={12} color="gray" /> : undefined}
                    data={PRICE_BY_OPTIONS.map(option => ({
                        label: t(option.label),
                        value: option.value,
                    }))}
                    onChange={value => {
                        if (value) {
                            setSavingField('price_by');
                            updateServiceMutation.mutate(
                                { id: service.id, price_by: value as Service['price_by'] },
                                { onSettled: () => setSavingField(null) }
                            );
                        }
                    }}
                />
                <Select
                    required
                    error={!service.cost_by}
                    size="xs"
                    className="w-44"
                    label={t('cost_by')}
                    value={service.cost_by}
                    disabled={savingField === 'cost_by' && updateServiceMutation.isPending}
                    rightSection={savingField === 'cost_by' && updateServiceMutation.isPending ? <Loader size={12} color="gray" /> : undefined}
                    data={PRICE_BY_OPTIONS.map(option => ({
                        label: t(option.label),
                        value: option.value,
                    }))}
                    onChange={value => {
                        if (value) {
                            setSavingField('cost_by');
                            updateServiceMutation.mutate(
                                { id: service.id, cost_by: value as Service['cost_by'] },
                                { onSettled: () => setSavingField(null) }
                            );
                        }
                    }}
                />
                <div className="h-[30px] flex items-center">
                    <Switch
                        size="xs"
                        color="black"
                        disabled={savingField === 'show_retired' && updateServiceMutation.isPending}
                        checked={Boolean(service.show_retired)}
                        onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            setSavingField('show_retired');
                            updateServiceMutation.mutate(
                                { id: service.id, show_retired: checked },
                                { onSettled: () => setSavingField(null) }
                            );
                        }}
                        label={t('show_retired')}
                    />
                </div>
            </div>

            {/* Compact Toolbar: Status filter tabs, Provider selector */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
                    <button
                        type="button"
                        onClick={() => setStatusFilter('all')}
                        className={`px-2.5 py-1 rounded-md font-medium text-xs transition-colors whitespace-nowrap cursor-pointer ${statusFilter === 'all' ? 'bg-gray-900 text-white shadow-2xs' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                    >
                        {t('all')} ({kpis.totalCount})
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('active')}
                        className={`px-2.5 py-1 rounded-md font-medium text-xs transition-colors whitespace-nowrap cursor-pointer ${statusFilter === 'active' ? 'bg-emerald-600 text-white shadow-2xs' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                    >
                        {t('status_active')} ({kpis.activeCount})
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('upcoming')}
                        className={`px-2.5 py-1 rounded-md font-medium text-xs transition-colors whitespace-nowrap cursor-pointer ${statusFilter === 'upcoming' ? 'bg-blue-600 text-white shadow-2xs' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                    >
                        {t('upcoming')} ({kpis.upcomingCount})
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('past')}
                        className={`px-2.5 py-1 rounded-md font-medium text-xs transition-colors whitespace-nowrap cursor-pointer ${statusFilter === 'past' ? 'bg-gray-600 text-white shadow-2xs' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                    >
                        {t('expired')} ({kpis.pastCount})
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    <ServiceNewPrice
                        service={service}
                    />
                    <ServiceNewCost
                        service={service}
                    />
                </div>
            </div>

            {/* Table with Passenger Tiers as Columns */}
            {filteredPeriods.length === 0 ? (
                <div className="text-center py-10 px-4 rounded-xl border border-dashed border-gray-200 bg-gray-50/50">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2 text-gray-400">
                        <IconScale size={20} stroke={1.5} />
                    </div>
                    <div className="text-xs font-semibold text-gray-900">{t('no_periods_found')}</div>
                </div>
            ) : (
                <div className="border border-gray-200 rounded-xl overflow-hidden shadow-2xs bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs tabular-nums">
                            <thead className="bg-gray-50/90 border-b border-gray-200 text-gray-500 font-semibold uppercase tracking-wider text-xxs select-none">
                                <tr>
                                    <th className="py-2.5 px-3">
                                        <div className="inline-flex items-center gap-1.5">
                                            <Tooltip
                                                label={allCollapsed ? t('expand_all_costs') : t('collapse_all_costs')}
                                                withArrow
                                            >
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="gray"
                                                    size="xs"
                                                    onClick={toggleAllCosts}
                                                >
                                                    {allCollapsed ? <IconChevronsDown size={14} stroke={1.8} /> : <IconChevronsUp size={14} stroke={1.8} />}
                                                </ActionIcon>
                                            </Tooltip>
                                            {renderSortHeader(`${t('period')}`, 'date')}
                                        </div>
                                    </th>
                                    {tierColumns.map(col => (
                                        <th key={col.key} className="py-2.5 px-3 text-right">
                                            {col.key === 'default'
                                                ? renderSortHeader(t(col.labelKey), 'price', true)
                                                : t(col.labelKey)}
                                        </th>
                                    ))}
                                    <th className="py-2.5 pr-3 pl-1 text-right w-1">
                                        {/* {t('actions')} */}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredPeriods.map(period => {
                                    const isCurrent = period.isCurrent;
                                    const isPast = period.isPast;
                                    const isCollapsed = collapsedPeriodIds.has(period.id);
                                    const dateRangeLabel = `${period.start_day ? formatDay(period.start_day) : '—'} – ${period.end_day ? formatDay(period.end_day) : '—'}`;

                                    return (
                                        <Fragment key={period.id}>
                                            {/* Primary Row: Sell Price */}
                                            <tr
                                                className={`transition-colors bg-white hover:bg-gray-50/70 ${isPast ? 'opacity-75 hover:opacity-100' : ''}`}
                                            >
                                                {/* Period / Sell Price Header */}
                                                <td className="py-2.5 px-3 font-semibold text-gray-900 whitespace-nowrap">
                                                    <div className="flex items-center gap-1.5">
                                                        <Tooltip
                                                            label={`${isCollapsed ? t('show_costs') : t('hide_costs')} (${dateRangeLabel})`}
                                                            withArrow
                                                        >
                                                            <ActionIcon
                                                                variant="subtle"
                                                                color="gray"
                                                                size="xs"
                                                                onClick={() => togglePeriodCollapse(period.id)}
                                                            >
                                                                {isCollapsed ? <IconChevronRight size={14} stroke={1.8} /> : <IconChevronDown size={14} stroke={1.8} />}
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <span
                                                            className={`w-2 h-2 rounded-full shrink-0 ${isCurrent
                                                                ? 'bg-emerald-500'
                                                                : isPast
                                                                    ? 'bg-gray-300'
                                                                    : 'bg-blue-500'
                                                                }`}
                                                            title={isCurrent ? t('status_active') : isPast ? t('expired') : t('upcoming')}
                                                        />
                                                        <span>
                                                            {formatDay(period.start_day)} – {formatDay(period.end_day)}
                                                        </span>
                                                        {isCollapsed && period.comparisons.length > 0 && (
                                                            <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.2 rounded-full">
                                                                {period.comparisons.length}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Sell Price Tiers */}
                                                {tierColumns.map(col => {
                                                    const tierData = period.priceTiers.find(item => item.tier === col.key);

                                                    if (!tierData || tierData.priceAmount === null) {
                                                        return (
                                                            <td key={col.key} className="py-2.5 px-3 text-right whitespace-nowrap">
                                                                {col.key === 'default' ? (
                                                                    <span className="text-amber-600 font-normal italic text-xxs">
                                                                        {t('price_status_missing')}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-gray-300">—</span>
                                                                )}
                                                            </td>
                                                        );
                                                    }

                                                    return (
                                                        <td key={col.key} className="py-2.5 px-3 text-right whitespace-nowrap">
                                                            <span className="font-bold text-gray-900 text-xs">
                                                                {formatPrice(tierData.priceAmount, { currency: tierData.priceCurrency })}
                                                            </span>
                                                        </td>
                                                    );
                                                })}

                                                {/* Sell Price Actions */}
                                                <td className="py-2.5 pr-3 pl-1 text-right whitespace-nowrap">
                                                    {(period.price || period.comparisons.some(c => c.cost?.id)) && (
                                                        <div className="inline-flex items-center justify-end gap-1">
                                                            {period.price && (
                                                                <>
                                                                    <ServiceNewCost service={service} initialPrice={period.price} compact />
                                                                    <EditSinglePrice service={service} price={period.price} />
                                                                </>
                                                            )}
                                                            {period.comparisons.some(c => c.cost?.id) && (
                                                                <Tooltip
                                                                    label={`${t('delete_all_costs')} (${dateRangeLabel})`}
                                                                    withArrow
                                                                >
                                                                    <ActionIcon
                                                                        variant="subtle"
                                                                        color="orange"
                                                                        size="sm"
                                                                        loading={deletingAllCostsPeriodId === period.id}
                                                                        onClick={() => handleDeleteAllCosts(period)}
                                                                    >
                                                                        <IconTrashX size={14} stroke={1.5} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            )}
                                                            {period.price?.id && (
                                                                <Tooltip
                                                                    label={`${t('delete_price')} (${period.start_day ? formatDay(period.start_day) : '—'} – ${period.end_day ? formatDay(period.end_day) : '—'})`}
                                                                    withArrow
                                                                >
                                                                    <ActionIcon
                                                                        variant="subtle"
                                                                        color="red"
                                                                        size="sm"
                                                                        loading={deletePriceMutation.isPending && deletePriceMutation.variables === period.price.id}
                                                                        onClick={() => deletePriceMutation.mutate(period.price!.id!)}
                                                                    >
                                                                        <IconTrash size={14} stroke={1.5} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>

                                            {/* Sub-rows: Provider Costs */}
                                            {!isCollapsed && (
                                                period.comparisons.length === 0 ? (
                                                    <tr className={`bg-gray-50/50 border-t border-gray-100 ${isPast ? 'opacity-70' : ''}`}>
                                                        <td colSpan={tierColumns.length + 2} className="py-1.5 pl-8 pr-3 text-xxs text-amber-600 italic">
                                                            <div className="flex items-center gap-1.5">
                                                                <IconCornerDownRight size={12} className="text-gray-400 shrink-0" />
                                                                <span>{t('no_cost_for_period')}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    period.comparisons.map((comp, compIdx) => {
                                                        const rowKey = `${period.id}_cost_${comp.cost?.id || compIdx}`;
                                                        const costDateDiffers = Boolean(
                                                            comp.cost &&
                                                            (comp.cost.start_day !== period.start_day || comp.cost.end_day !== period.end_day)
                                                        );

                                                        return (
                                                            <tr
                                                                key={rowKey}
                                                                className={`bg-gray-50/60 hover:bg-gray-100/70 border-t border-gray-100 transition-colors ${isPast ? 'opacity-75 hover:opacity-100' : ''}`}
                                                            >
                                                                {/* Indented Provider + Cost Dates (if different) */}
                                                                <td className="py-2 pl-7 pr-3 text-gray-700 whitespace-nowrap">
                                                                    <div className="flex flex-col">
                                                                        <div className="flex items-center gap-1.5">
                                                                            <IconCornerDownRight size={13} className="text-gray-400 shrink-0" />
                                                                            {comp.provider ? (
                                                                                <div className="flex items-center gap-1.5">
                                                                                    {comp.provider.category && <TagIcon name={comp.provider.category} size="xxs" hideText />}
                                                                                    <span className="font-medium text-gray-800">{comp.provider.name}</span>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-gray-500 italic text-xxs">{t('all_providers')}</span>
                                                                            )}
                                                                        </div>
                                                                        {costDateDiffers && comp.cost && (
                                                                            <span className="pl-5 text-[10px] text-gray-400 font-normal leading-tight mt-0.5">
                                                                                {comp.cost.start_day ? formatDay(comp.cost.start_day) : '—'} – {comp.cost.end_day ? formatDay(comp.cost.end_day) : '—'}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* Provider Cost Tiers + Margin */}
                                                                {tierColumns.map(col => {
                                                                    const tierData = comp.tiers.find(item => item.tier === col.key);

                                                                    if (!tierData || tierData.costAmount === null) {
                                                                        return (
                                                                            <td key={col.key} className="py-2 px-3 text-right whitespace-nowrap text-gray-300">
                                                                                —
                                                                            </td>
                                                                        );
                                                                    }

                                                                    const hasBoth = !tierData.isDifferentCurrencies && tierData.priceAmount !== null && tierData.costAmount !== null && tierData.priceAmount > 0;
                                                                    const marginPct = hasBoth ? 100 - (tierData.costAmount! / tierData.priceAmount!) * 100 : null;

                                                                    return (
                                                                        <td key={col.key} className="py-2 px-3 text-right whitespace-nowrap">
                                                                            <div className="inline-flex items-center justify-end gap-1.5">
                                                                                {marginPct !== null && (
                                                                                    <span
                                                                                        className={`text-[10px] font-medium px-1 py-0.2 rounded ${tierData.hasLoss
                                                                                            ? 'text-red-600 bg-red-50 border border-red-100'
                                                                                            : 'text-emerald-700 bg-emerald-50 border border-emerald-100'
                                                                                            }`}
                                                                                    >
                                                                                        {marginPct.toFixed(1)}%
                                                                                    </span>
                                                                                )}
                                                                                <span className="font-medium text-gray-700">
                                                                                    {formatPrice(tierData.costAmount, { currency: tierData.costCurrency })}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                    );
                                                                })}

                                                                {/* Provider Cost Actions */}
                                                                <td className="py-2 pr-3 pl-1 text-right whitespace-nowrap">
                                                                    {comp.cost && (
                                                                        <div className="inline-flex items-center justify-end gap-1">
                                                                            <EditSingleCost service={service} cost={comp.cost} />
                                                                            {comp.cost.id && (
                                                                                <Tooltip
                                                                                    label={`${t('delete_cost')} (${comp.cost.start_day ? formatDay(comp.cost.start_day) : '—'} – ${comp.cost.end_day ? formatDay(comp.cost.end_day) : '—'})`}
                                                                                    withArrow
                                                                                >
                                                                                    <ActionIcon
                                                                                        variant="subtle"
                                                                                        color="red"
                                                                                        size="sm"
                                                                                        loading={deleteCostMutation.isPending}
                                                                                        onClick={() => deleteCostMutation.mutate(comp.cost!.id!)}
                                                                                    >
                                                                                        <IconTrash size={14} stroke={1.5} />
                                                                                    </ActionIcon>
                                                                                </Tooltip>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ))}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
});
